/**
 * Loads everything the Brand Updates 2026 home page renders from.
 *
 * Extracted from `routes/authenticated/dashboard` so the two surfaces that show
 * this page — the beta branch of `/dashboard` (inside the existing app chrome)
 * and `/reimagined/home-2026` (the full-page version with the 2026 sidebar) —
 * fetch it identically. The field mapping that turns this into view data lives
 * in `utils/beta/home-model.ts`; this module only does the fetching.
 */
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { ContributionActionItem } from 'tio-employee/services/reimagined-data';
import type { ReimaginedHomeModel } from 'tio-employee/routes/authenticated/reimagined/home';

/**
 * Everything the beta home page (`components/beta/home`) renders from.
 *
 * `reimagined` is the shared snapshot bundle; the two extra fields cover
 * figures the 2026 design asks for that the bundle does not carry.
 */
export interface BetaHomeRouteModel {
  reimagined: ReimaginedHomeModel;
  /** Server-authored loan-repayment action items for "Needs Your Attention". */
  actionItems: ContributionActionItem[];
  /** Tuition assistance used this benefit year; null when unresolvable. */
  tuitionYearToDate: number | null;
}

export async function loadBetaHomeModel(
  reimaginedData: ReimaginedDataService
): Promise<BetaHomeRouteModel> {
  const [
    financialSnapshot,
    dataSourceInfo,
    employerContributions,
    pslfData,
    tasData,
    section127Info,
    // The last two feed figures the snapshot bundle does not carry. Neither is
    // load-bearing for the page, so a failure must degrade rather than blank
    // the dashboard — see how each is unwrapped below.
    contributionHistory,
    tasUsedAmount,
  ] = await Promise.all([
    reimaginedData.fetchFinancialSnapshot(),
    reimaginedData.getDataSourceInfo(),
    reimaginedData.fetchEmployerContributions(),
    reimaginedData.fetchPslfData(),
    reimaginedData.fetchTasData(),
    reimaginedData.fetchSection127Info(),
    reimaginedData.fetchContributionHistory().catch((error: unknown) => {
      console.warn('[beta home] contribution history unavailable', error);
      return null;
    }),
    reimaginedData.fetchTasUsedAmount().catch((error: unknown) => {
      console.warn('[beta home] TA year-to-date unavailable', error);
      return null;
    }),
  ]);

  const reimagined: ReimaginedHomeModel = {
    financialSnapshot,
    dataSourceInfo,
    employerContributions,
    pslfData,
    tasData,
    section127Info,
  };

  /**
   * `fetchTasUsedAmount` swallows its own errors and resolves
   * `{ yearToDate: 0, isLiveData: false }`, so the `.catch()` above can never
   * turn a failed request into null on its own — a TAS/OpenSearch outage would
   * otherwise render a confident `$0` for an unknown amount.
   *
   * `isLiveData: false` also covers demo mode and a missing employee id, so
   * only the non-demo case becomes null: in a demo the fixture value IS the
   * intended number.
   */
  const tuitionYearToDate =
    tasUsedAmount && (tasUsedAmount.isLiveData || reimaginedData.isDemoMode)
      ? tasUsedAmount.yearToDate
      : null;

  return {
    reimagined,
    actionItems: contributionHistory?.actionItems ?? [],
    tuitionYearToDate,
  };
}
